import React, { useMemo } from 'react';
import {
    Box,
    Stack,
    Divider,
    Collapse,
    TextField,
    Autocomplete,
    Button
} from '@mui/material';
import { CloseOutlined } from '@ant-design/icons';
import Table from '../common/Table';
import useLocalTableFilters from '../../hooks/useLocalTableFilters';
import { uniqueSorted } from '../../utils/array';

const EMPTY_FILTERS = { owner: '', plate: '', state: '', make: '', model: '' };

const VehicleTable = ({
    vehicles,
    members,
    onVehicleClick,
    globalFilter,
    showFilters = false
}) => {
    const { filters, handleFilterChange, handleResetFilters, hasActiveFilters } =
        useLocalTableFilters(EMPTY_FILTERS, showFilters);

    const ownerNameByUid = useMemo(() => {
        const map = new Map();
        (members || []).forEach(m => map.set(m.uid, `${m.first_name} ${m.last_name}`));
        return map;
    }, [members]);

    const stateOptions = useMemo(() => uniqueSorted(vehicles, 'state'), [vehicles]);
    const makeOptions = useMemo(() => uniqueSorted(vehicles, 'make'), [vehicles]);

    const filteredVehicles = useMemo(() => {
        if (!showFilters) return vehicles;
        const owner = filters.owner.trim().toLowerCase();
        const plate = filters.plate.trim().toLowerCase();
        const model = filters.model.trim().toLowerCase();
        return (vehicles || []).filter(vehicle => {
            if (owner) {
                const ownerName = ownerNameByUid.get(vehicle.ownerId) || 'N/A';
                if (!ownerName.toLowerCase().includes(owner)) return false;
            }
            if (plate && !(vehicle.tag || '').toLowerCase().includes(plate)) return false;
            if (filters.state && vehicle.state !== filters.state) return false;
            if (filters.make && vehicle.make !== filters.make) return false;
            if (model && !(vehicle.model || '').toLowerCase().includes(model))
                return false;
            return true;
        });
    }, [vehicles, ownerNameByUid, filters, showFilters]);

    const columns = useMemo(
        () => [
            {
                Header: 'Owner',
                accessor: row => ownerNameByUid.get(row.ownerId) || 'N/A',
                Cell: ({ row }) => ownerNameByUid.get(row.original.ownerId) || 'N/A'
            },
            {
                Header: 'Plate #',
                accessor: 'tag',
                Cell: ({ value }) => value.toUpperCase()
            },
            {
                Header: 'State',
                accessor: 'state'
            },
            {
                Header: 'Make',
                accessor: 'make'
            },
            {
                Header: 'Model',
                accessor: 'model'
            }
        ],
        [ownerNameByUid]
    );

    return (
        <>
            <Collapse in={showFilters}>
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', md: 'flex-start' }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fill, minmax(200px, 1fr))',
                                gap: 1.5
                            }}
                        >
                            <TextField
                                label="Owner"
                                size="small"
                                fullWidth
                                value={filters.owner}
                                onChange={e =>
                                    handleFilterChange('owner', e.target.value)
                                }
                            />
                            <TextField
                                label="Plate #"
                                size="small"
                                fullWidth
                                value={filters.plate}
                                onChange={e =>
                                    handleFilterChange('plate', e.target.value)
                                }
                            />
                            <Autocomplete
                                options={stateOptions}
                                value={filters.state || null}
                                onChange={(_, value) =>
                                    handleFilterChange('state', value || '')
                                }
                                size="small"
                                renderInput={params => (
                                    <TextField {...params} label="State" />
                                )}
                            />
                            <Autocomplete
                                options={makeOptions}
                                value={filters.make || null}
                                onChange={(_, value) =>
                                    handleFilterChange('make', value || '')
                                }
                                size="small"
                                renderInput={params => (
                                    <TextField {...params} label="Make" />
                                )}
                            />
                            <TextField
                                label="Model"
                                size="small"
                                fullWidth
                                value={filters.model}
                                onChange={e =>
                                    handleFilterChange('model', e.target.value)
                                }
                            />
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CloseOutlined />}
                            onClick={handleResetFilters}
                            disabled={!hasActiveFilters}
                            sx={{ flexShrink: 0 }}
                        >
                            Reset
                        </Button>
                    </Stack>
                </Box>
                <Divider />
            </Collapse>
            <Table
                columns={columns}
                data={filteredVehicles}
                onRowClick={onVehicleClick}
                externalGlobalFilter={globalFilter}
            />
        </>
    );
};

export default VehicleTable;
